/*
 * Apache Guacamole's browser client, guacamole-common-js 1.6.0 (Apache-2.0),
 * vendored unchanged in `public/vendor/guacamole-common-js-1.6.0/` from the
 * Apache release on Maven Central (docs/rdp.md, "Browser client"). It is a
 * classic script that defines `window.Guacamole`; we load it on the first
 * remote desktop only. Below are the parts of its API we use: it ships no
 * types.
 */

export const GUACAMOLE_SCRIPT = `${import.meta.env.BASE_URL}vendor/guacamole-common-js-1.6.0/all.min.js`;

export interface GuacStatus {
  code: number;
  message: string;
}

export interface GuacMouseState {
  x: number;
  y: number;
  left: boolean;
  middle: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
}

export interface GuacMouse {
  onEach(types: string[], listener: (event: { state: GuacMouseState }) => void): void;
}

export interface GuacKeyboard {
  /** Return false to stop the browser's own handling of the key. */
  onkeydown: ((keysym: number) => boolean) | null;
  onkeyup: ((keysym: number) => void) | null;
  /** Releases every key it thinks is held (after the page loses focus). */
  reset(): void;
}

export interface GuacDisplay {
  getElement(): HTMLElement;
  getWidth(): number;
  getHeight(): number;
  getScale(): number;
  scale(scale: number): void;
  onresize: ((width: number, height: number) => void) | null;
  oncursor: ((canvas: HTMLCanvasElement, x: number, y: number) => void) | null;
  showCursor(shown: boolean): void;
  /** Every layer drawn into one new canvas (the assistant's screen snapshot). */
  flatten(): HTMLCanvasElement;
}

/** A stream guacd opened (e.g. its clipboard). */
export type GuacInputStream = object;
/** A stream we opened toward guacd (e.g. our clipboard). */
export type GuacOutputStream = object;

export interface GuacStringReader {
  ontext: ((text: string) => void) | null;
  onend: (() => void) | null;
}

export interface GuacStringWriter {
  sendText(text: string): void;
  sendEnd(): void;
}

/** What `Guacamole.Client` needs of a tunnel (`rdp/tunnel.ts` is ours). */
export interface GuacTunnel {
  state: number;
  uuid: string | null;
  receiveTimeout: number;
  unstableThreshold: number;
  oninstruction: ((opcode: string, args: string[]) => void) | null;
  onstatechange: ((state: number) => void) | null;
  onerror: ((status: GuacStatus) => void) | null;
  onuuid: ((uuid: string) => void) | null;
  connect(data?: string): void;
  disconnect(): void;
  sendMessage(...elements: unknown[]): void;
  isConnected(): boolean;
  setState(state: number): void;
  setUUID(uuid: string): void;
}

export interface GuacClient {
  getDisplay(): GuacDisplay;
  connect(data?: string): void;
  disconnect(): void;
  sendKeyEvent(pressed: 0 | 1, keysym: number): void;
  sendMouseState(state: GuacMouseState, applyDisplayScale?: boolean): void;
  sendSize(width: number, height: number): void;
  createClipboardStream(mimetype: string): GuacOutputStream;
  onclipboard: ((stream: GuacInputStream, mimetype: string) => void) | null;
  onerror: ((status: GuacStatus) => void) | null;
}

export interface GuacamoleApi {
  Client: new (tunnel: GuacTunnel) => GuacClient;
  Keyboard: new (element: Element) => GuacKeyboard;
  Mouse: (new (element: Element) => GuacMouse) & {
    Touchscreen: new (element: Element) => GuacMouse;
  };
  StringReader: new (stream: GuacInputStream) => GuacStringReader;
  StringWriter: new (stream: GuacOutputStream) => GuacStringWriter;
}

/** `Guacamole.Tunnel.State`. */
export const TUNNEL_STATE = { connecting: 0, open: 1, closed: 2, unstable: 3 } as const;

let loading: Promise<GuacamoleApi> | null = null;

/** Loads guacamole-common-js once (a script of our origin: CSP `script-src 'self'`). */
export function loadGuacamole(): Promise<GuacamoleApi> {
  loading ??= new Promise<GuacamoleApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GUACAMOLE_SCRIPT;
    script.async = true;
    script.onload = () => {
      const api = (window as unknown as { Guacamole?: GuacamoleApi }).Guacamole;
      if (api) resolve(api);
      else reject(new Error("guacamole-common-js did not load"));
    };
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error("guacamole-common-js did not load"));
    };
    document.head.append(script);
  });
  return loading;
}
